/**
 * Message Level Encryption (MLE) Service
 *
 * Thin state-management wrapper around @farmvest/nested-jwt-sdk.
 * The SDK owns all crypto logic; this file owns session state:
 *   clientKeyPair    — generated on Login mount, cleared on logout
 *   serverPublicKeys — received from the login response
 *   publicKeyPem     — client public key PEM sent to server at login
 *   jtiCache         — tracks seen response JTIs to prevent replay attacks
 *
 * Lifecycle (called by Login.tsx and farmvest_api.ts):
 *   1. initialize()          — Login mount  → generates RSA-2048 key pair
 *   2. getPublicKeyPem()     — include as client_public_key in loginWithOtp body
 *   3. setServerPublicKey()  — called after login response with server_public_key
 *   4. isReady()             → true once both keys are set; MLE is now active
 *   5. encryptRequest()      — axios request interceptor (POST/PUT/PATCH)
 *   6. decryptResponse()     — axios response interceptor (secure_response field)
 *   7. clearKeys()           — logout / 401
 */

import {
  generateClientKeyPair,
  exportPublicKeyAsPem,
  importServerPublicKeyFromPem,
  buildRequestToken,
  parseResponseToken,
  JtiCache,
} from '@farmvest/nested-jwt-sdk';
import type { ClientKeyPair, ServerPublicKeys } from '@farmvest/nested-jwt-sdk';

// ─── Session state ────────────────────────────────────────────────────────────

let clientKeyPair: ClientKeyPair | null = null;
let serverPublicKeys: ServerPublicKeys | null = null;
let publicKeyPem: string | null = null;

// Shared in-flight restore promise. When multiple parallel requests trigger
// restoreSession after a page refresh, they all join this single promise
// instead of each starting their own IDB read race.
let restorePromise: Promise<boolean> | null = null;

const TOKEN_TTL_SECONDS = 300;

// JtiCache window matches token TTL so a token cannot be replayed before expiry.
const jtiCache = new JtiCache(TOKEN_TTL_SECONDS);

const TOKEN_CONFIG = {
  tokenTtlSeconds: TOKEN_TTL_SECONDS,
  clockSkewSeconds: 300,
  enableReplayPrevention: true,
} as const;

// ─── IndexedDB Storage ────────────────────────────────────────────────────────
const DB_NAME = 'mle-keys-db';
const STORE_NAME = 'mle-store';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function saveToIDB(key: string, value: any): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.error(`[MLE] Failed to save ${key} to IDB`, err);
  }
}

async function getFromIDB(key: string): Promise<any> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.error(`[MLE] Failed to get ${key} from IDB`, err);
    return null;
  }
}

async function clearIDB(): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.error(`[MLE] Failed to clear IDB`, err);
  }
}

// ─── Initialisation ───────────────────────────────────────────────────────────

/**
 * Generate a fresh RSA-2048 client key pair and cache the public key PEM.
 * Called once on Login mount so everything is ready before the user submits OTP.
 * Safe to call again — replaces the old pair and clears the server key.
 */
async function initialize(): Promise<void> {
  console.log('[MLE] initialize: generating client key pair...');
  clientKeyPair = await generateClientKeyPair();
  console.log(clientKeyPair, "clientKeyPair");
  publicKeyPem = await exportPublicKeyAsPem(clientKeyPair.publicKeyJwk);
  serverPublicKeys = null;

  // Clear any stale keys before writing the new pair.
  await clearIDB();
  await saveToIDB('clientKeyPair', clientKeyPair);
  await saveToIDB('publicKeyPem', publicKeyPem);

  console.log('[MLE] initialized');
}

// ─── Server key exchange ──────────────────────────────────────────────────────

/**
 * Import the server's PEM public key from the login response (server_public_key).
 * Once set, isReady() returns true and all POST/PUT/PATCH requests are encrypted.
 */
async function setServerPublicKey(pem: string): Promise<void> {
  serverPublicKeys = await importServerPublicKeyFromPem(pem);
  await saveToIDB('serverPublicKeys', serverPublicKeys);
}

// ─── Status ───────────────────────────────────────────────────────────────────

function isReady(): boolean {
  const mleEnabledStr = localStorage.getItem('mle_enabled');
  const isEnabled = mleEnabledStr === 'true';
  return isEnabled && clientKeyPair !== null && serverPublicKeys !== null;
}

/** Returns the client public key PEM to send as client_public_key at login. */
function getPublicKeyPem(): string | null {
  return publicKeyPem;
}

/**
 * Reads keys from IndexedDB and assigns them to module-level state.
 * Called only via restoreSession() which memoizes concurrent calls.
 */
async function doRestoreFromIDB(): Promise<boolean> {
  console.log('[MLE] Attempting to restore session from IndexedDB...');
  const [ck, sk, pk] = await Promise.all([
    getFromIDB('clientKeyPair'),
    getFromIDB('serverPublicKeys'),
    getFromIDB('publicKeyPem'),
  ]);

  // Validate the restored data has the correct structure (new SDK format).
  // Old stub stored { publicKeyJwk, privateKey } — reject and clear if stale.
  const validKeys =
    ck && sk && pk &&
    ck.signingKey instanceof CryptoKey &&
    ck.decryptionKey instanceof CryptoKey &&
    sk.verificationKey instanceof CryptoKey &&
    sk.encryptionKey instanceof CryptoKey;

  if (validKeys) {
    clientKeyPair = ck;
    serverPublicKeys = sk;
    publicKeyPem = pk;
    console.log('[MLE] Successfully restored keys from IndexedDB.');
    return true;
  }

  // Clear stale/incompatible IDB data so the next login starts fresh.
  await clearIDB();
  console.log('[MLE] Stale or incompatible keys in IndexedDB — cleared. Re-login required.');
  return false;
}

/**
 * Attempts to restore keys from IndexedDB (e.g. after a page refresh).
 * Memoizes the in-flight promise so N concurrent callers share one restore.
 */
async function restoreSession(): Promise<boolean> {
  if (isReady()) return true;
  if (restorePromise) return restorePromise;

  restorePromise = doRestoreFromIDB().finally(() => {
    restorePromise = null;
  });
  return restorePromise;
}

// ─── Encrypt / decrypt ────────────────────────────────────────────────────────

/**
 * Sign + encrypt an outbound request payload.
 * Returns { encrypted_payload: "<compact JWE>" } — the body shape the backend expects.
 */
async function encryptRequest(
  payload: Record<string, unknown>,
): Promise<{ encrypted_payload: string }> {
  if (!clientKeyPair || !serverPublicKeys) {
    throw new Error('[MLE] Cannot encrypt — service not ready');
  }

  const { token } = await buildRequestToken({
    payload,
    clientKeyPair,
    serverPublicKeys,
    config: TOKEN_CONFIG,
  });

  return { encrypted_payload: token };
}

/**
 * Decrypt + verify an inbound response token.
 * Pass the value of response.data.secure_response.
 */
async function decryptResponse<T extends Record<string, unknown> = Record<string, unknown>>(
  token: string,
): Promise<T> {
  if (!clientKeyPair || !serverPublicKeys) {
    throw new Error('[MLE] Cannot decrypt — service not ready');
  }

  const payload = await parseResponseToken<T>({
    token,
    clientKeyPair,
    serverPublicKeys,
    config: TOKEN_CONFIG,
    jtiCache,
  });

  return payload as T;
}

// ─── Cleanup ──────────────────────────────────────────────────────────────────

async function clearKeys(): Promise<void> {
  clientKeyPair = null;
  serverPublicKeys = null;
  publicKeyPem = null;
  // Must await so the IDB transaction commits before any route change /
  // remount triggers Login.initialize() and writes fresh keys on top.
  try {
    await clearIDB();
  } catch {
    // swallow — in-memory state is already null, IDB will be overwritten
    // on next login regardless.
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const mleService = {
  initialize,
  setServerPublicKey,
  isReady,
  restoreSession,
  getPublicKeyPem,
  encryptRequest,
  decryptResponse,
  clearKeys,
};
